import {Batch, Container, StorableSummaryExtras} from 'constants/SampleConstants';
import {BioentitySummary} from 'folder-items/FileConstants';
import {StructuredData} from 'notebook/data-tables/DataTableConstants';
import {FromServerId, ServerEntryVersion} from 'notebook/ELNConstants';
import * as net from 'util/Net';
import * as TaskRunner from 'util/TaskRunner';
import {STAGE_RUN_STATUS} from 'workflows/constants/StageRunConstants';
import {CreateSpecifier, EditSpecifier} from 'workflows/modals/ConfigureStageRunsModal';
import {
  StageConfiguration,
  StageEntryDetails,
  StageEntryDocument,
  StageFlowNode,
  StageRun,
  StageRunSample,
  StageRunSampleKey,
  WorkflowDetails,
  WorkflowStage,
} from 'workflows/WorkflowConstants';

export interface FetchStageRunSamplesResponse {
  stageRunSamples: Array<StageRunSample>;
  batchesById: {[batchId: string]: Batch};
  containersById: {[containerId: string]: Container};
  fileSummariesById: {[bioentityId: string]: BioentitySummary};
  storableSummaryExtras?: StorableSummaryExtras;
}

export function fetchStageRunSamples(
  stageRunSampleIds: Array<string>,
  {
    includeBatches,
    includeStorableSummaryExtras,
  }: {
    includeBatches?: boolean;
    includeStorableSummaryExtras?: boolean;
  }
): JQueryPromise<FetchStageRunSamplesResponse> {
  return net.postJSON({
    url: '/1/api/workflows/stage-run-samples:bulk-get',
    data: {stageRunSampleIds, includeBatches, includeStorableSummaryExtras},
  });
}

export function deleteStageRun(
  stageRunId: string,
  {shouldDeleteDownstreamRuns}: {shouldDeleteDownstreamRuns: boolean}
): JQueryPromise<WorkflowDetails> {
  return net.delete_({
    url: `/1/api/workflows/stage-runs/${stageRunId}`,
    data: {shouldDeleteDownstreamRuns},
  });
}

interface SplitByInputsResponse {
  affectedStageRuns: Array<StageRun>;
  affectedStageFlowNodes: Array<StageFlowNode>;
  affectedStageEntries: Array<StageEntryDocument>;
  affectedStageConfigurations: Array<StageConfiguration>;
}

export function splitByInputs(workflowStage: WorkflowStage): Promise<SplitByInputsResponse> {
  return net.postJSONPromise({
    url: '/1/api/workflows/stage-runs:split-by-inputs',
    data: {workflowStageId: workflowStage.id},
  });
}

interface BulkConfigureStageRunsResponse {
  workflowDetails: WorkflowDetails;
  affectedStageEntryIds: Array<string>;
}

// This is the response from the
// /workflows/stage-runs:bulk-update-status call.  It contains the
// to_dicts for each entry and run which were affected by the status
// call
interface BulkUpdateStageRunsResponse {
  affectedStageEntries: Array<StageEntryDocument>;
  affectedStageRuns: Array<StageRun>;
}

export interface UpdateStatusSpecifier {
  stageRunId: string;
  status: STAGE_RUN_STATUS;
}

export function bulkConfigureStageRuns({
  createSpecifiers,
  editSpecifiers,
  workflowId,
}: {
  createSpecifiers?: Array<CreateSpecifier>;
  editSpecifiers?: Array<EditSpecifier>;
  workflowId?: string;
}): Promise<BulkConfigureStageRunsResponse> {
  return net.postJSONPromise({
    url: '/1/api/workflows/stage-runs:bulk-configure',
    data: {createSpecifiers, editSpecifiers, workflowId},
  });
}

export function bulkUpdateStatusStageRuns({
  updateStatusSpecifiers,
}: {
  updateStatusSpecifiers: Array<UpdateStatusSpecifier>;
}): Promise<BulkUpdateStageRunsResponse> {
  return Promise.resolve(
    TaskRunner.postJSON({
      url: '/1/api/workflows/stage-runs:bulk-update-status',
      data: {updateStatusSpecifiers},
    })
  );
}

interface OutputSamplesResponse {
  stageRuns: Array<StageRun>;
  entryIdToOrderedOutputSampleKeys: {[entryId: string]: Array<StageRunSampleKey>};
  // TODO(ala): entryIdToStructuredData is temporarily optional until we remove it entirely
  entryIdToStructuredData?: {[entryId: string]: StructuredData};
}

export function addOutputSamples(sampleKeys: Array<StageRunSampleKey>): JQueryPromise<OutputSamplesResponse> {
  return net.postJSON({
    url: `/1/api/workflows/stage-runs:bulk-add-output-samples`,
    data: {
      sampleKeys,
    },
  });
}

// Remove the specified samples from the stage runs outputs.
// 'samplesToRemove' is a list of dicts of the form {stageRunId, stageRunSampleId}
export function bulkRemoveOutputSamples(
  samplesToRemove: Array<StageRunSampleKey>
): JQueryPromise<OutputSamplesResponse> {
  return net.postJSON({
    url: '/1/api/workflows/stage-runs:bulk-remove-output-samples',
    data: {
      samplesToRemove,
    },
  });
}

export interface RerunStageRunResponse {
  affectedStageRuns: Array<StageRun>;
  affectedStageFlowNodes: Array<StageFlowNode>;
  affectedStageEntries: Array<StageEntryDocument>;
  affectedStageConfigurations: Array<StageConfiguration>;
  // TODO(ala): entryIdToStructuredData is temporarily optional until we remove it entirely
  entryIdToStructuredData?: {[entryId: string]: StructuredData};
}

export function rerunStageRun(stageRunId: string): JQueryPromise<RerunStageRunResponse> {
  return net.postJSON({
    url: `/1/api/workflows/stage-runs/${stageRunId}:rerun`,
  });
}

export interface ReplaceInputSampleParams {
  sampleIdToRemove?: string;
  containerIdToAdd?: string | null;
  sampleIdToAdd?: string;
  batchIdToAdd?: string | null;
  bioentityIdToAdd?: string | null;
}

export interface ReplaceInputSampleResponse {
  stageRun: StageRun;
  stageEntryDetails: StageEntryDetails;
  entryVersion: ServerEntryVersion<FromServerId>;
}

export function replaceInputSample(
  stageRunId: string,
  args: ReplaceInputSampleParams
): Promise<ReplaceInputSampleResponse> {
  const sampleIdToRemove = args.sampleIdToRemove;
  const containerIdToAdd = args.containerIdToAdd;
  // eslint-disable-next-line benchlint/no-in-operator
  const sampleIdToAdd = 'sampleIdToAdd' in args ? args.sampleIdToAdd : undefined;
  // eslint-disable-next-line benchlint/no-in-operator
  const batchIdToAdd = 'batchIdToAdd' in args ? args.batchIdToAdd : undefined;
  // eslint-disable-next-line benchlint/no-in-operator
  const bioentityIdToAdd = 'bioentityIdToAdd' in args ? args.bioentityIdToAdd : undefined;
  return net.postJSONPromise({
    url: `/1/api/workflows/stage-runs/${stageRunId}:replace-input-sample`,
    data: {
      sampleIdToRemove,
      sampleIdToAdd,
      batchIdToAdd,
      bioentityIdToAdd,
      containerIdToAdd,
    },
  });
}

interface IdsForSample {
  batchId: string | null;
  containerId: string | null;
  fileId: string | null;
}

export interface BulkAddInputSamplesResponse {
  stageRun: StageRun;
  stageRunSamples: Array<StageRunSample>;
  stageEntryDetails: StageEntryDetails;
  entryVersion: ServerEntryVersion<FromServerId>;
  storableSummaryExtras: StorableSummaryExtras;
}

export function bulkAddInputSamples(
  stageRunId: string,
  {
    samplesToAdd,
  }: {
    samplesToAdd: Array<IdsForSample>;
  }
): Promise<BulkAddInputSamplesResponse> {
  return net.postJSONPromise({
    url: `/1/api/workflows/stage-runs/${stageRunId}:bulk-add-input-samples`,
    data: {
      samplesToAdd,
    },
  });
}

export interface BulkRemoveInputSamplesResponse {
  stageRuns: Array<StageRun>;
  stageEntryDetails: StageEntryDetails;
  entryVersion: ServerEntryVersion<FromServerId>;
}

export function bulkRemoveInputSamples(
  stageEntryId: string,
  samplesToRemove: Array<StageRunSampleKey>
): JQueryPromise<BulkRemoveInputSamplesResponse> {
  return net.postJSON({
    url: `/1/api/workflows/stage-runs:bulk-remove-input-samples`,
    data: {
      stageEntryId,
      samplesToRemove,
    },
  });
}

export interface AutoselectInputSampleContainersResponse {
  numContainersAutoselected: number;
  stageRuns: Array<StageRun>;
  stageEntryDetails: StageEntryDetails;
  entryVersion: ServerEntryVersion<FromServerId>;
}

export function autoselectInputSampleContainers(
  stageEntryId: string,
  selectedStageRunsAndSamples: Array<StageRunSampleKey>
): Promise<AutoselectInputSampleContainersResponse> {
  return net.postJSONPromise({
    url: '/1/api/workflows/stage-runs:autoselect-input-sample-containers',
    data: {
      stageEntryId,
      selectedStageRunsAndSamples,
    },
  });
}
